import React, { forwardRef } from 'react';

import type { ListItemElement } from '@aline/common';
import { isListItem } from '@aline/common';
import { Editor } from 'slate';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import { BaseMenuOption } from '../BaseMenuOption';
import type { CommonMenuItemProps } from '../types';

export const ContinueListParagraphMenuItem = forwardRef(function ContinueListParagraphMenuItem(
  props: CommonMenuItemProps,
  ref: React.Ref<HTMLLIElement>
): JSX.Element | null {
  const { closeRootMenu, ...rest } = props;
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  function continueListParagraph(): void {
    if (!editingMode || !currentUserId) return closeRootMenu();

    AuthorizedTransforms.continueList(editor, {
      authorId: currentUserId,
      editingMode,
    });
    ClientTransforms.focus(editor);
    closeRootMenu();
  }

  const listItemEntries = Editor.nodes<ListItemElement>(editor, {
    match: isListItem,
  });
  const isListItemSelected = Boolean(listItemEntries.next().value);

  if (!isListItemSelected) return null;

  return (
    <BaseMenuOption
      ref={ref}
      label="Continue numbering"
      onClick={continueListParagraph}
      {...rest}
    />
  );
});

export const RestartListParagraphMenuItem = forwardRef(function ContinueListParagraphMenuItem(
  props: CommonMenuItemProps,
  ref: React.Ref<HTMLLIElement>
): JSX.Element | null {
  const { closeRootMenu, ...rest } = props;
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  function restartListParagraph(): void {
    if (!editingMode || !currentUserId) return closeRootMenu();

    AuthorizedTransforms.restartList(editor, {
      authorId: currentUserId,
      editingMode,
    });
    ClientTransforms.focus(editor);
    closeRootMenu();
  }

  const listItemEntries = Editor.nodes<ListItemElement>(editor, {
    match: isListItem,
  });
  const isListItemSelected = Boolean(listItemEntries.next().value);

  if (!isListItemSelected) return null;

  return (
    <BaseMenuOption ref={ref} label="Restart numbering" onClick={restartListParagraph} {...rest} />
  );
});
